import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { LoadingBoxComponent } from '@shared/components/loading-box/loading-box.component';
import { TimelineHistoricoComponent, TimelineHistoricoItem } from '@shared/components/timeline-historico/timeline-historico.component';
import { OrigemOcorrenciaEnum } from '@shared/enums/vendas/origem-ocorrencia.enum';
import { VendaTipoOcorrenciaEnum } from '@shared/enums/vendas/venda-tipo-ocorrencia.enum';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { ProcessoVendaService } from '@shared/services/apis/vendas/processo-venda.service';
import { CriarVendaOcorrenciaRequest } from '@shared/services/requests/vendas/criar-venda-ocorrencia.request';
import { VendaOcorrenciaStruct } from '@shared/services/structs/vendas/venda-ocorrencia.struct';

/**
 * Seção Histórico do documento gravado: as ocorrências em `app-timeline-historico`, inline como na OP,
 * com filtros, anotação e exclusão da anotação nas mesmas regras do "Ver histórico" (`pv-historico-dialog`).
 */
@Component({
    selector: 'app-vd-historico',
    standalone: true,
    imports: [MatIconModule, LoadingBoxComponent, TimelineHistoricoComponent],
    templateUrl: './vd-historico.component.html',
    styleUrl: './vd-historico.component.css',
})
export class VdHistoricoComponent implements OnInit, OnChanges {
    @ViewChild(TimelineHistoricoComponent) timeline?: TimelineHistoricoComponent;

    @Input({ required: true }) public idVenda!: number;

    /** Permissão de alterar o processo de venda (198): libera a anotação e a exclusão dela. */
    @Input() public podeAlterar: boolean = false;

    /** `isBloquearExclusaoOcorrencia` da configuração do Pedido de Venda: trava a exclusão da anotação. */
    @Input() public bloquearExclusaoOcorrencia: boolean = true;

    /** Muda quando o "Ver histórico" aberto pelo topo fecha: o histórico pode ter mudado lá. */
    @Input() public pedidoRecarregar: number = 0;

    public isLoading = true;
    public isRegistrandoAnotacao = false;
    public itens: TimelineHistoricoItem[] = [];
    public itemExcluindo: TimelineHistoricoItem | null = null;

    /** O item da linha do tempo não conhece a API: o id da ocorrência fica com o hospedeiro. */
    private idOcorrenciaPorItem = new Map<TimelineHistoricoItem, number>();

    constructor(
        private processoVendaService: ProcessoVendaService,
        private alertService: AlertService,
        private dialog: MatDialog,
    ) {}

    public ngOnInit(): void {
        this.carregar();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (changes['pedidoRecarregar'] && !changes['pedidoRecarregar'].firstChange) this.carregar();
    }

    /** O skeleton é só da primeira carga; depois de anotar ou excluir, a lista troca no lugar. */
    private carregar(): void {
        this.processoVendaService.getHistorico(this.idVenda).subscribe({
            next: res => {
                this.isLoading = false;
                if (res.isError) {
                    this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
                    return;
                }
                this.idOcorrenciaPorItem.clear();
                this.itens = (res.listaOcorrencia ?? []).map(ocorrencia => {
                    const item = this.paraItem(ocorrencia);
                    this.idOcorrenciaPorItem.set(item, ocorrencia.idVendaOcorrencia);
                    return item;
                });
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Erro', 'Não foi possível carregar o histórico.', AlertType.error);
            },
        });
    }

    /**
     * Mesmo item do `pv-historico-dialog`: sem usuário, o autor é a origem; justificativa da mudança de
     * etapa "<motivo> - <texto do usuário>"; só a anotação informa `podeExcluir`.
     */
    private paraItem(ocorrencia: VendaOcorrenciaStruct): TimelineHistoricoItem {
        const justificativa = [ocorrencia.nomeMotivoTransicao, ocorrencia.justificativa].filter(texto => !!texto).join(' - ');
        const ehAnotacao = ocorrencia.idVendaTipoOcorrencia === VendaTipoOcorrenciaEnum.Anotacao;

        return {
            icone: ocorrencia.nomeIconeTipoOcorrencia,
            titulo: ocorrencia.nomeTipoOcorrencia,
            descricao: ocorrencia.descricao,
            detalhe: ocorrencia.detalhes,
            justificativa: justificativa || null,
            quando: ocorrencia.dataOcorrencia,
            quemNome: ocorrencia.nomeUsuario || ocorrencia.nomeOrigemOcorrencia,
            nomeOrigem: ocorrencia.nomeOrigemOcorrencia,
            iconeOrigem: ocorrencia.nomeIconeOrigemOcorrencia,
            exibirOrigem: ocorrencia.idOrigemOcorrencia !== OrigemOcorrenciaEnum.Usuario,
            nomeStatusAnterior: ocorrencia.nomeEtapaKanbanAnterior,
            nomeStatusAtual: ocorrencia.nomeEtapaKanbanAtual,
            podeExcluir: ehAnotacao ? this.podeAlterar && !this.bloquearExclusaoOcorrencia : undefined,
            motivoNaoExcluivel: ehAnotacao ? this.motivoNaoExcluivel : null,
        };
    }

    private get motivoNaoExcluivel(): string | null {
        if (!this.podeAlterar) return 'Sem permissão para alterar o documento.';
        if (this.bloquearExclusaoOcorrencia) return 'A exclusão de ocorrências está bloqueada nas configurações do Pedido de Venda.';

        return null;
    }

    /** Anotação do dia vai sem data, para o servidor gravar a hora do registro; retroativa leva o dia escolhido. */
    public registrarAnotacao(anotacao: { descricao: string; dataOcorrencia: Date }): void {
        const request = new CriarVendaOcorrenciaRequest();
        request.descricao = anotacao.descricao;
        request.dataOcorrencia = anotacao.dataOcorrencia.toDateString() === new Date().toDateString() ? null : anotacao.dataOcorrencia;

        this.isRegistrandoAnotacao = true;
        this.processoVendaService.criarOcorrencia(this.idVenda, request).subscribe({
            next: res => {
                this.isRegistrandoAnotacao = false;
                if (res.isError) {
                    this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
                    return;
                }
                this.timeline?.limparAnotacao();
                this.alertService.show('Sucesso', 'Anotação registrada.', AlertType.success);
                this.carregar();
            },
            error: () => {
                this.isRegistrandoAnotacao = false;
                this.alertService.show('Erro', 'Não foi possível registrar a anotação.', AlertType.error);
            },
        });
    }

    public excluirAnotacao(item: TimelineHistoricoItem): void {
        const idVendaOcorrencia = this.idOcorrenciaPorItem.get(item);
        if (idVendaOcorrencia == null || this.itemExcluindo) return;

        this.dialog
            .open(ConfirmModalComponent, {
                width: '440px',
                data: {
                    title: 'Excluir anotação',
                    description: 'Tem certeza que deseja excluir esta anotação do histórico?',
                    buttonConfirm: 'Excluir',
                    buttonClose: 'Cancelar',
                },
            })
            .afterClosed()
            .subscribe(resultado => {
                if (!resultado?.confirm) return;

                this.itemExcluindo = item;
                this.processoVendaService.excluirOcorrencia(this.idVenda, idVendaOcorrencia).subscribe({
                    next: res => {
                        this.itemExcluindo = null;
                        if (res.isError) {
                            this.alertService.show('Aviso', res.errorDescription, AlertType.warning);
                            return;
                        }
                        this.alertService.show('Sucesso', 'Anotação excluída.', AlertType.success);
                        this.carregar();
                    },
                    error: () => {
                        this.itemExcluindo = null;
                        this.alertService.show('Erro', 'Não foi possível excluir a anotação.', AlertType.error);
                    },
                });
            });
    }
}
